import { Box, IconButton } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import type { FieldArrayWithId } from "react-hook-form"
import { useFormContext } from "react-hook-form"

import type { CustomerProductFileArrayFormValues } from "./schemas"

import { Form } from "@/components/Forms/Form/Form"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import FileArea from "@/components/upload/components/FileArea"
import {
  ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC,
  IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
} from "@/components/upload/constants"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import {
  CUSTOMER_PRODUCT_FILE_FIELD_GROUPS,
  CUSTOMER_PRODUCT_FILE_TYPE_MAPPING,
} from "@/screens/CustomerProducts/constants"
import type { Option } from "@/types/form-fields"

const fileTypeOptions: Option<FieldGroupEnum>[] = Object.entries(CUSTOMER_PRODUCT_FILE_TYPE_MAPPING).map(
  ([id, config]) => ({
    value: id as FieldGroupEnum,
    label: config.label,
  })
)

interface CustomerProductFileFormProps {
  fields: FieldArrayWithId<CustomerProductFileArrayFormValues, "files">[]
  onRemove: (index: number) => void
}

export const CustomerProductFileForm: FC<CustomerProductFileFormProps> = ({ fields, onRemove }) => {
  const { control } = useFormContext<CustomerProductFileArrayFormValues>()

  return (
    <Form data-testid='add-customer-product-file-form'>
      {fields.map((field, index) => (
        <Box key={field.id} className='flex flex-row items-start gap-4'>
          <Box className='grow'>
            <FormSelect<CustomerProductFileArrayFormValues, `files.${number}.fileType`, FieldGroupEnum>
              control={control}
              name={`files.${index}.fileType`}
              label='Document Type'
              required
              placeholder='Select One'
              options={fileTypeOptions}
              data-testid={`customer-product-files.${index}.fileType`}
              className='mb-3'
            />
            <FormTextField
              control={control}
              name={`files.${index}.fileDescription`}
              label='Document Description'
              placeholder='Document Description'
              multiline
              data-testid={`customer-product-files.${index}.fileDescription`}
              className='mb-3'
            />
            <FileArea
              name={`files.${index}.file`}
              fieldGroups={CUSTOMER_PRODUCT_FILE_FIELD_GROUPS}
              referenceTableName={ReferenceTableName.CustomerProductPreference}
              showUploadAreaByDefault
              allowedMimeTypes={IMAGE_MIME_TYPES_WITH_PDF_AND_DOC}
              allowedTypesLabel={ALLOWED_TYPE_LABEL_IMAGES_WITH_PDF_AND_DOC}
              uploadAreaTitle='Click to upload'
            />
          </Box>
          {fields.length > 1 && (
            <IconButton
              onClick={() => onRemove(index)}
              data-testid={`customer-product-files.remove-button-${index}`}
              className='mt-[20px] text-gray-900'
              size='medium'
            >
              <DeleteOutlined fontSize='medium' />
            </IconButton>
          )}
        </Box>
      ))}
    </Form>
  )
}
